import { providers } from 'ethers'
import { createPublicClient, http, PublicClient, Transport, Chain } from 'viem'

import { getRpcUrl, getViemChain, SupportedChainId } from '~/constants/chains'
import { EvmAddress } from '~/utils/address'
import { getServerOrigin } from '~/utils/serverOrigin'

/**
 * Retries a function n number of times with exponential backoff before giving up
 */
export async function retryExponentialBackoff<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends (...arg0: any[]) => any,
>(
  fn: T,
  args: Parameters<T>,
  options: {
    maxTry: number
    retryCount?: number
  },
): Promise<ReturnType<T>> {
  const { maxTry, retryCount = 1 } = options
  const currRetry = typeof retryCount === 'number' ? retryCount : 1
  try {
    const result = await fn(...args)
    return result
  } catch (e) {
    if (currRetry >= maxTry) {
      throw e
    }

    await delay(1000 * Math.pow(2, retryCount - 1))
    return retryExponentialBackoff(fn, args, {
      maxTry,
      retryCount: currRetry + 1,
    })
  }
}

const delay: (timeoutMs: number) => void = async (timeoutMs) => {
  await new Promise((resolve) =>
    // Add a random 0 - 100 ms to timeout to avoid requests syncing up
    setTimeout(resolve, timeoutMs + getRandomTimeMs(100)),
  )
}

const getRandomTimeMs: (maxMs: number) => number = (maxMs) => {
  return Math.random() * maxMs
}

const PUBLIC_CLIENT_MAP: {
  [key: string]: PublicClient<Transport, Chain>
} = {}

export const getReadPublicClient = (
  chainId: number,
): PublicClient<Transport, Chain> => {
  if (!PUBLIC_CLIENT_MAP[chainId]) {
    PUBLIC_CLIENT_MAP[chainId] = createPublicClient({
      chain: getViemChain(chainId),
      transport: http(getRpcUrl(chainId)),
    })
  }

  return PUBLIC_CLIENT_MAP[chainId]
}

const CLIENT_VERSION =
  process.env.GIT_COMMIT_HASH && process.env.GIT_COMMIT_HASH.length > 0
    ? process.env.GIT_COMMIT_HASH
    : 'unknown'

export const getAxiosConfig = () => ({
  baseURL: getServerOrigin(),
  timeout: 15_000,
  headers: {
    'X-Client-Version': CLIENT_VERSION,
  },
  ...(process.env.APP_ENV === 'development'
    ? {
        proxy: {
          protocol: 'http',
          host: '127.0.0.1',
          port: 8080,
        },
      }
    : {}),
})

export const getPathWithParams = (
  path: string,
  params: { [key: string]: string | boolean | number },
) => {
  const paramsString = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&')
  return `${path}${paramsString ? `?${paramsString}` : ''}`
}

export const hasBytecode = async (
  chainId: SupportedChainId,
  address: EvmAddress.Type,
) => {
  const publicClient = getReadPublicClient(chainId)

  const bytecode = await publicClient.getBytecode({ address })
  return !!bytecode
}

export const getEthersProvider = (chainId: SupportedChainId) => {
  return new providers.JsonRpcProvider(getRpcUrl(chainId))
}

// serialize bigints to strings by storing them as objects with a __bigint__ key
export const customSerializer = (data: unknown) =>
  JSON.stringify(data, (_, value) =>
    typeof value === 'bigint' ? { __bigint__: value.toString() } : value,
  )

// deserialize bigints from strings by parsing the __bigint__ key
export const customDeserializer = (str: string) =>
  JSON.parse(str, (_, value) =>
    value && typeof value === 'object' && '__bigint__' in value
      ? BigInt(value.__bigint__)
      : value,
  )

// Debounced batcher for batching multiple requests into a single request
type CreateDebouncedBatcherOpts<I, K extends string, O, R> = {
  windowMs: number
  toKey: (input: I) => K
  request: (keys: K[]) => Promise<Record<K, O> | Map<K, O>>
  select: (raw: O | undefined, key: K) => R | undefined
}

const createDebouncedBatcher = <I, K extends string, O, R>(
  opts: CreateDebouncedBatcherOpts<I, K, O, R>,
) => {
  const { windowMs, toKey, request, select } = opts

  type Resolver = {
    resolve: (v: R | undefined) => void
    reject: (e: unknown) => void
  }

  let pending = new Map<K, Resolver[]>()
  let timer: ReturnType<typeof setTimeout> | null = null

  const flush = async () => {
    const current = pending
    pending = new Map()
    timer = null
    if (current.size === 0) return

    const keys = Array.from(current.keys())

    try {
      const raw = await request(keys)
      const results =
        raw instanceof Map ? raw : new Map(Object.entries(raw) as [K, O][])
      current.entries().forEach(([key, resolvers]) => {
        const value = select(results.get(key), key)
        resolvers.forEach(({ resolve }) => resolve(value))
      })
    } catch (err) {
      current
        .values()
        .forEach((resolvers) => resolvers.forEach(({ reject }) => reject(err)))
    }
  }

  const call = (input: I): Promise<R | undefined> => {
    const key = toKey(input)
    return new Promise<R | undefined>((resolve, reject) => {
      const resolverArray = pending.get(key) ?? []
      resolverArray.push({ resolve, reject })
      pending.set(key, resolverArray)

      if (!timer) {
        timer = setTimeout(() => {
          // let errors propagate to individual promise reject handlers
          flush()
        }, windowMs)
      }
    })
  }

  return { call }
}

// Singleton batcher cache shared across all batcher instances
const batcherCache = new Map<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ReturnType<typeof createDebouncedBatcher<any, string, any, any>>
>()

export const createSingletonBatcher = <I, K extends string, O, R>(
  key: string,
  opts: CreateDebouncedBatcherOpts<I, K, O, R>,
): ReturnType<typeof createDebouncedBatcher<I, K, O, R>> => {
  if (!batcherCache.has(key)) {
    batcherCache.set(key, createDebouncedBatcher(opts))
  }

  return batcherCache.get(key) as ReturnType<
    typeof createDebouncedBatcher<I, K, O, R>
  >
}

export const clearBatcherCache = () => {
  batcherCache.clear()
}
